import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Fab,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  AssignmentInd,
  DeleteOutlined,
  Edit,
  EscalatorWarning,
  Search,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AuthState } from "store/features/auth";
import { RootState } from "store";
import { isNurseryAdmin } from "utils/roles";
import {
  getChildren as getChildrenApi,
  getChildDevelopment,
  getParents as getParentsApi,
} from "apis";
import { FormFields, Parent } from "types";
import DeleteDialog from "components/DeleteDialog";
import PreviewReportDialog from "components/PreviewReportDialog";
import { stringAvatar } from "utils";
import TablePaginationLocalizationProvider from "components/utils/TablePaginationLocalizationProvider";

type SortKey = "name" | "gender" | "dob";
type SortDir = "asc" | "desc";

const ChildrenManage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { role } = useSelector<RootState, AuthState>((store) => store.auth);
  const canMutate = isNurseryAdmin(role);

  const [children, setChildren] = useState<FormFields[]>([]);
  const [parents, setParents] = useState<Parent[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const navigate = useNavigate();
  const [devSummary, setDevSummary] = useState<
    Record<string, { assessed: number; total: number }>
  >({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FormFields>();
  const [selectedId, setSelectedId] = useState(0);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([getChildrenApi(), getParentsApi()])
      .then(([ch, pa]) => {
        setChildren(ch || []);
        setParents(pa || []);
        // Fetch development summaries for each child (filtered by age band)
        const kids = ch || [];
        Promise.all(
          kids.map((c: any) => {
            return getChildDevelopment(c.id)
              .then((areas: any[]) => {
                const bands = areas?.[0]?.ageBands ?? [];
                let ageBandId: number | null = null;
                if (c.dateOfBirth && bands.length) {
                  const months = dayjs().diff(dayjs(c.dateOfBirth), "month");
                  const found = bands.find(
                    (b: any) =>
                      months >= b.minMonths &&
                      (b.maxMonths === null || months < b.maxMonths),
                  );
                  ageBandId = found?.id ?? bands[bands.length - 1].id;
                }
                let total = 0;
                let assessed = 0;
                for (const area of areas || []) {
                  for (const obj of area.objectives || []) {
                    if (ageBandId !== null && obj.ageBandId !== ageBandId)
                      continue;
                    total++;
                    if (obj.status > 0) assessed++;
                  }
                }
                return { id: String(c.id), total, assessed };
              })
              .catch(() => ({ id: String(c.id), total: 0, assessed: 0 }));
          }),
        ).then((summaries) => {
          const map: Record<string, { assessed: number; total: number }> = {};
          for (const s of summaries)
            map[s.id] = { assessed: s.assessed, total: s.total };
          setDevSummary(map);
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDialogClose = useCallback(() => {
    setOpenDeleteDialog(false);
    setOpenReportDialog(false);
    fetchData();
  }, [fetchData]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return children;
    const term = search.toLowerCase();
    return children.filter(
      (c) =>
        c.firstName?.toLowerCase().includes(term) ||
        c.lastName?.toLowerCase().includes(term) ||
        c.fatherFirstName?.toLowerCase().includes(term) ||
        c.motherFirstName?.toLowerCase().includes(term),
    );
  }, [children, search]);

  const sorted = useMemo(() => {
    const list = filtered.slice();
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case "name":
          return dir * (a.firstName || "").localeCompare(b.firstName || "");
        case "gender":
          return dir * (a.gender || "").localeCompare(b.gender || "");
        case "dob":
          return (
            dir *
            (new Date(a.dateOfBirth || 0).getTime() -
              new Date(b.dateOfBirth || 0).getTime())
          );
        default:
          return 0;
      }
    });
    return list;
  }, [filtered, sortKey, sortDir]);

  return (
    <>
      {/* Header */}
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
          flexWrap: "wrap",
          py: 1.5,
        }}
      >
        <EscalatorWarning sx={{ color: "primary.main", fontSize: 24 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("child.label")}
        </Typography>
        <Chip
          label={children.length}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.06),
            fontWeight: 600,
          }}
        />
        <Box
          sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
        >
          <TextField
            size="small"
            placeholder={t("placeholders.select") + "..."}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.disabled" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 14 },
            }}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
        </Box>
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Container sx={{ py: 2 }}>
          {sorted.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={8}
              color="text.secondary"
            >
              <EscalatorWarning
                sx={{ fontSize: 64, mb: 2, color: "action.disabled" }}
              />
              <Typography variant="h6" gutterBottom>
                {search ? t("attendance.no_match") : t("child.empty")}
              </Typography>
              {!search && canMutate && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate("/child/add")}
                  sx={{ mt: 2, textTransform: "none", fontWeight: 600 }}
                >
                  {t("child.add")}
                </Button>
              )}
            </Box>
          ) : (
            <Card
              variant="outlined"
              sx={{ borderRadius: 3, overflow: "hidden" }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "name"}
                          direction={sortKey === "name" ? sortDir : "asc"}
                          onClick={() => handleSort("name")}
                        >
                          {t("inputs.first_name")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("inputs.last_name")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "gender"}
                          direction={sortKey === "gender" ? sortDir : "asc"}
                          onClick={() => handleSort("gender")}
                        >
                          {t("inputs.gender")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("inputs.parent")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <TableSortLabel
                          active={sortKey === "dob"}
                          direction={sortKey === "dob" ? sortDir : "asc"}
                          onClick={() => handleSort("dob")}
                        >
                          {t("inputs.birthday")}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("inputs.classroom")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {t("grading.development")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">
                        {t("labels.action")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((child) => (
                        <TableRow key={child.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              <Avatar
                                src={child.photo as string | undefined}
                                {...(!child.photo && child.firstName
                                  ? stringAvatar(
                                      `${child.firstName} ${child.lastName}`,
                                      { width: 32, height: 32, fontSize: 13 },
                                    )
                                  : {
                                      sx: {
                                        width: 32,
                                        height: 32,
                                        fontSize: 13,
                                      },
                                    })}
                              />
                              <Typography variant="body2" fontWeight={500}>
                                {child.firstName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {child.lastName || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                child.gender === "Male"
                                  ? t("gender.male")
                                  : t("gender.female")
                              }
                              color={
                                child.gender === "Male"
                                  ? "primary"
                                  : "secondary"
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {[
                                (child as any).parent?.firstName,
                                (child as any).parent?.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {child.dateOfBirth
                                ? dayjs(child.dateOfBirth).format("DD MMM YYYY")
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {(child as any).classroom?.name || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const s = devSummary[String(child.id)];
                              const pct =
                                s && s.total > 0
                                  ? Math.round((s.assessed / s.total) * 100)
                                  : 0;
                              const barColor =
                                pct >= 50
                                  ? theme.palette.success.main
                                  : pct > 0
                                    ? theme.palette.warning.main
                                    : theme.palette.action.disabled;
                              return (
                                <Box
                                  onClick={() =>
                                    navigate(`/child/${child.id}/development`)
                                  }
                                  sx={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    px: 1.25,
                                    py: 0.75,
                                    borderRadius: 2,
                                    transition: "background 0.15s",
                                    "&:hover": {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.06,
                                      ),
                                    },
                                  }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 48 }}>
                                    <Box
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: alpha(barColor, 0.15),
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          height: "100%",
                                          width: `${pct}%`,
                                          borderRadius: 3,
                                          bgcolor: barColor,
                                          transition: "width 0.3s ease",
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    sx={{
                                      color: barColor,
                                      minWidth: 28,
                                      textAlign: "right",
                                    }}
                                  >
                                    {pct}%
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {canMutate ? (
                              <>
                                <Tooltip title={t("labels.edit")}>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      navigate("/child/edit", { state: child })
                                    }
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("buttons.delete")}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setSelectedItem(child);
                                      setSelectedId(
                                        parseInt(child.id as string),
                                      );
                                      setOpenDeleteDialog(true);
                                    }}
                                  >
                                    <DeleteOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("report.label")}>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => {
                                      setSelectedItem(child);
                                      setSelectedId(
                                        parseInt(child.id as string),
                                      );
                                      setOpenReportDialog(true);
                                    }}
                                  >
                                    <AssignmentInd fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider />
              <TablePaginationLocalizationProvider>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={sorted.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </TablePaginationLocalizationProvider>
            </Card>
          )}
        </Container>
      )}

      {/* Add FAB */}
      {canMutate && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            pl: 2.5,
            pr: 3,
            gap: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: "0 4px 14px 0 rgba(0,0,0,0.25)",
            "&:hover": { boxShadow: "0 6px 20px 0 rgba(0,0,0,0.3)" },
          }}
          onClick={() => navigate("/child/add")}
        >
          <Add />
          {t("child.add")}
        </Fab>
      )}

      {/* Dialogs */}
      {openDeleteDialog && selectedItem && (
        <DeleteDialog
          open={openDeleteDialog}
          onClose={handleDialogClose}
          item={selectedItem}
        />
      )}
      {openReportDialog && selectedItem && (
        <PreviewReportDialog
          open={openReportDialog}
          onClose={handleDialogClose}
          id={selectedId}
          child={selectedItem}
        />
      )}
    </>
  );
};

export default ChildrenManage;
